import React, { createContext, useState, useEffect, useContext, useRef } from "react";
import { useLocation } from "react-router-dom";
import { serverGet } from "../../middleware/http";
import { apiConst, appConst } from "../constant/constant";
import { getSearchParams } from "../utils/validation";

const DoctorContext = createContext(null);

export const useDoctor = () => {
    return useContext(DoctorContext);
};

export const DoctorProvider = ({ children }) => {
    const [doctorContext, setDoctorContext] = useState(null);
    const [memberId, setMemberId] = useState(null);
    const [doctorContextLoader, setDoctorContextLoader] = useState(false);

    const location = useLocation();

    // The profile response varies with the pricing attributes built from spid/ftypeid, so the
    // key covers those too — a step that only rewrites unrelated URL params reuses what is
    // already loaded, while a genuine change to member/speciality/type still refetches.
    const buildProfileKey = (id) => {
        const member_id = (id ? id : getSearchParams("memberid")) ?? "";
        return `${member_id.toString().toLowerCase()}|${getSearchParams("spid") ?? ""}|${getSearchParams("ftypeid") ?? ""}`;
    };

    // Holds the request for the profile currently loaded or in flight. Every caller for the same
    // key joins that one request instead of issuing its own, which is what stops this context and
    // the page components that also ask for the profile from fetching it twice per step.
    const profileRequestRef = useRef({ key: null, promise: null });

    const isProviderProfileLoaded = (id) => {
        const request = profileRequestRef.current;
        return !!request.promise && request.key === buildProfileKey(id);
    };

    const providerprofile = async (id) => {
        const profileKey = buildProfileKey(id);

        if (isProviderProfileLoaded(id)) {
            return await profileRequestRef.current.promise;
        }

        const request = (async () => {
            try {
                let member_id = getSearchParams("memberid");
                setDoctorContextLoader(true);

                const _typeId = getSearchParams("ftypeid");
                const specialityId = getSearchParams("spid");

                const pricingAtr = [
                    {
                        ServiceTypeId: 110,
                        Quantity: 1,
                        Attributes: [
                            {
                                attributeid: 101,
                                recordtypeid: 33,
                                recordid: _typeId ? _typeId : 1,
                            },
                            {
                                attributeid: 102,
                                recordtypeid: 32,
                                recordid: specialityId,
                            },
                        ],
                    },
                ];

                if (id) {
                    member_id = id?.toLowerCase();
                }
                const res = await serverGet(`${apiConst.getProviderProfile}/${member_id}?atr=${JSON.stringify(pricingAtr)}`);
                if (res?.data !== null && res?.status == 1) {
                    setDoctorContext(res?.data);
                } else if (profileRequestRef.current.key === profileKey) {
                    // A failed lookup is not an answer worth keeping — drop it so the next caller
                    // asks again rather than inheriting the failure for the rest of the flow.
                    profileRequestRef.current = { key: null, promise: null };
                }
                return res;
            } catch (e) {
                console.error(e);
                if (profileRequestRef.current.key === profileKey) {
                    profileRequestRef.current = { key: null, promise: null };
                }
            } finally {
                setDoctorContextLoader(false);
            }
        })();

        profileRequestRef.current = { key: profileKey, promise: request };

        return await request;
    };

    // to the target member via the MemberAccess-guarded API. Returns true when granted;
    // on denial the API returns 403 and the global HTTP layer redirects to
    // /page-access-denied, so this resolves false (fail-closed) for any non-success.
    const checkProviderAccess = async (id) => {
        try {
            const _memberId = id ?? getSearchParams("memberid");
            if (!_memberId) return false;
            const res = await serverGet(`${apiConst.checkAccess}${_memberId}`);
            return res?.status === 1;
        } catch (e) {
            console.error(e);
            return false;
        }
    };

    const member_id = getSearchParams("memberid");
    let _brtype = getSearchParams("brtype");
    const isConsultWithoutProvider = _brtype === appConst?.resourceCodes.consultNowWithoutProvider;

    useEffect(() => {
        // "Consult now without provider" starts with no provider chosen, and this clears out any
        // provider left over from an earlier selection so none is shown. Once one has actually
        // been assigned it arrives on the URL as memberid, and from that point the booking has a
        // provider like any other — clearing then threw away a profile that the very next page
        // turns around and fetches again.
        if (isConsultWithoutProvider && !member_id) {
            profileRequestRef.current = { key: null, promise: null };
            if (doctorContext) setDoctorContext(null);
            return;
        }

        if (!member_id || location.pathname === "/profiledetail") return;

        if (isProviderProfileLoaded()) return;

        setMemberId(member_id);
        if (doctorContext) setDoctorContext(null);
        providerprofile();
    }, [location?.search, member_id, _brtype]);

    return <DoctorContext.Provider value={{ doctorContext, providerprofile, doctorContextLoader, checkProviderAccess, isProviderProfileLoaded }}>{children}</DoctorContext.Provider>;
};
